import {isAxiosError} from 'axios'
import i18n, {TKey} from '@/translations/i18n'
import {toast} from '@utilities/toast'
import {forwardRef, ReactNode, Ref, RefAttributes} from 'react'
import {extendTailwindMerge} from 'tailwind-merge'
import {type ClassValue, clsx} from 'clsx'
import {z} from 'zod'
import i18next from 'i18next'

export const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

type ReactSelect = {
    label: string
    value: string | number
}
export const retrieveSelectSingleValue = (options: ReactSelect[], value: string) => {
    return options.find(option => option.value.toString() == value)
}

export const retrieveSelectMultiValues = (options: ReactSelect[], values: [string]) => {
    return options.filter(option => values.find(value => value.toString() == option.value.toString()))
}

export const getInitials = (name: string) => {
    return name.match(/\b\w/g) || []
}

export const megabytesToBytes = (megabytes: number) => megabytes * 1_000_000

/**
 * Checks that a path coming from an untrusted source (eg. a search param) points inside the app,
 * so it can be safely used as a redirect target without opening an open redirect.
 * Accepts only paths starting with a single slash: `//evil.com`, `https://evil.com`,
 * `javascript:` and backslash tricks are all rejected.
 */
export const isInternalPath = (path?: string): path is string => {
    if (!path || !path.startsWith('/')) {
        return false
    }
    // control characters are stripped by browsers when resolving urls, so they could be used
    // to smuggle a protocol relative url past the check below
    const hasControlChars = [...path].some(character => {
        const charCode = character.charCodeAt(0)
        return charCode <= 0x1f || charCode == 0x7f
    })
    if (hasControlChars) {
        return false
    }
    // reject protocol relative urls (//evil.com) and backslash variants (/\evil.com)
    return !/^\/[\\/]/.test(path)
}

/**
 * Function that throws an error, it's useful to prevent impossible states in the application.
 * Example: const id = params.id ?? raise('No id provided'), in this case id is a string instead of string | undefined
 * @param error
 */
export const raise = (error: string): never => {
    throw new Error(error)
}

export const debounce = <T extends (...args: Parameters<T>) => ReturnType<T>>(
    callback: T,
    delay: number
): ((...args: Parameters<T>) => void) => {
    let timeout: ReturnType<typeof setTimeout>

    return (...args: Parameters<T>) => {
        clearTimeout(timeout)
        timeout = setTimeout(() => {
            callback(...args)
        }, delay)
    }
}

export const errorHandler = (error: unknown) => {
    if (isAxiosError(error)) {
        const message =
            error.response?.data.message && error.status != 500
                ? i18n.t(`api_errors:${error.response.data.message}` as TKey)
                : i18n.t('errors:default')
        return toast.error(message)
    }

    toast.error(i18n.t('errors:default'))
}

// i18next stores locale tags with underscores (e.g. `pt_BR`), but `Intl`
// requires BCP-47 tags with hyphens (`pt-BR`), otherwise it throws a RangeError.
export const getIntlLocale = () => i18next.language.replace(/_/g, '-')

export const formatCurrency = (amount = 0, currency: string) => {
    const language = i18n.language.split('_')[0]

    return new Intl.NumberFormat(language, {
        style: 'currency',
        currency
    }).format(amount)
}

export function genericForwardRef<TRef, TProps = object>(
    render: (props: TProps, ref: Ref<TRef>) => ReactNode
): (props: TProps & RefAttributes<TRef>) => ReactNode {
    return forwardRef(render) as (props: TProps & RefAttributes<TRef>) => ReactNode
}

/**
 * maskPhoneNumber
 * @param phoneNumber
 */
export const maskPhoneNumber = (phoneNumber: string) => {
    const phoneNumberWithoutSpaces = phoneNumber.replace(/ /g, '') // Remove all spaces
    const prefix = phoneNumberWithoutSpaces.slice(0, 5) // Get the first 3 digits
    const suffix = phoneNumberWithoutSpaces.slice(phoneNumberWithoutSpaces.length - 2) // Get the last 2 digits
    return `${prefix}*****${suffix}` // Combine the masked digits
}

export const ObjectKeys = Object.keys as <T extends object>(object: T) => Array<keyof T>
export const ObjectValues = Object.values as <T extends object>(object: T) => Array<T[keyof T]>
export const ObjectEntries = Object.entries as <T extends object>(
    object: T
) => Array<{[K in keyof T]: [K, T[K]]}[keyof T]>

//Chatbot
export const isFromPull = (steps: {id: string}[]) => {
    const history = Object.values(steps).reverse()

    const pullIndex = history.findIndex(({id}) => id.includes('pull'))
    const startIndex = history.findIndex(({id}) => id.includes('start'))

    return pullIndex >= 0 && pullIndex < startIndex
}

const customTwMerge = extendTailwindMerge({
    extend: {
        classGroups: {
            'font-size': [
                'text-display-2xl',
                'text-display-xl',
                'text-display-lg',
                'text-display-md',
                'text-display-sm',
                'text-display-sm-fixed',
                'text-display-xs',
                'text-display-xs-fixed',
                'text-xl-fixed'
            ]
            // https://github.com/dcastil/tailwind-merge/blob/v2.3.0/docs/recipes.md#adding-custom-scale-from-tailwind-config-to-tailwind-merge-config
            // https://github.com/dcastil/tailwind-merge/blob/v2.3.0/docs/configuration.md
        }
    }
})

export function cn(...inputs: ClassValue[]) {
    return customTwMerge(clsx(inputs))
}

/**
 * Matches any character outside the Basic Multilingual Plane (code point > U+FFFF).
 *
 * These are the 4-byte UTF-8 characters (emoji like 👋, and other astral-plane symbols) that the
 * backend's utf8mb3 columns cannot store — inserting them fails with MySQL error 3988
 * "Conversion from utf8mb4 into utf8mb3 impossible". BMP symbols such as ★ ♥ ✓ are 3 bytes and
 * store fine, so they are intentionally allowed.
 */
export const NON_BMP_REGEX = /[\u{10000}-\u{10FFFF}]/u

export const hasUnsupportedChars = (value: string) => NON_BMP_REGEX.test(value)

/**
 * Zod refinement that rejects strings containing characters the backend cannot store (any code
 * point outside the BMP). Reusable across any textarea/input form.
 *
 * Attach it to opt in; omit it to skip the check:
 *   notes: noUnsupportedChars(z.string())   // validated
 *   notes: z.string()                        // not validated
 *
 * Works with nullable/optional strings — empty values pass. The schema's inferred type is
 * preserved exactly, so it composes with .min()/.max()/.optional()/.nullable().
 */
export const noUnsupportedChars = <T extends z.ZodType<string | null | undefined, z.ZodTypeDef, unknown>>(
    schema: T
): z.ZodEffects<T, z.output<T>, z.input<T>> =>
    schema.refine((value: string | null | undefined) => !value || !hasUnsupportedChars(value), {
        message: i18n.t('errors:emojis_not_allowed')
    })
